import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type ToastTone = "info" | "success" | "error";

interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
}

interface ToastContextValue {
  notify: (message: string, tone?: ToastTone) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

let nextId = 1;

/**
 * Transient confirmations and failures.
 *
 * The region is a live region so screen readers announce a result that is
 * otherwise only visible: `assertive` for errors, which interrupt, and
 * `polite` for everything else, which waits for a pause.
 */
export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const notify = useCallback((message: string, tone: ToastTone = "info") => {
    const id = nextId;
    nextId += 1;
    setToasts((current) => [...current, { id, tone, message }]);
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ notify }), [notify]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="gw-toasts">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

const ToastItem = ({
  toast,
  onDismiss,
}: {
  toast: Toast;
  onDismiss: (id: number) => void;
}) => {
  useEffect(() => {
    // Errors stay longer: they usually require the reader to act on them.
    const timeout = window.setTimeout(
      () => onDismiss(toast.id),
      toast.tone === "error" ? 9000 : 5000,
    );
    return () => window.clearTimeout(timeout);
  }, [onDismiss, toast.id, toast.tone]);

  return (
    <div
      className={`gw-toast gw-toast--${toast.tone}`}
      role={toast.tone === "error" ? "alert" : "status"}
      aria-live={toast.tone === "error" ? "assertive" : "polite"}
    >
      <span style={{ flex: 1 }}>{toast.message}</span>
      <button
        type="button"
        className="gw-btn gw-btn--ghost gw-btn--sm"
        onClick={() => onDismiss(toast.id)}
        aria-label="Close"
      >
        ×
      </button>
    </div>
  );
};

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast must be used inside a ToastProvider");
  return context;
};
